import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';

const EVERGREEN_AAP_CHECKOUT_URL = 'https://gospelwiseauthor.com/checkout/EVERGREEN_AAP_PRODUCT_ID';
const HUB_WEBHOOK_URL = 'https://gospelwiseauthor.app/camp-fbwm-register.php';
const HUB_LOGIN_URL = 'https://www.gospelwiseauthor.app/#/login?redirect=https://camp.gospelwiseauthor.com';

function computeTrailStartDate() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const d = String(tomorrow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function Start() {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('new');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [evergreenOpen, setEvergreenOpen] = useState(true);
  const [checkingReg, setCheckingReg] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [existingEmail, setExistingEmail] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('settings_gwaacademy');
        const data = Array.isArray(res) ? res : res.data || [];
        const setting = data.find(
          (s) => s.key === 'evergreen_registration_open' || s.setting_key === 'evergreen_registration_open'
        );
        if (setting && setting.value === 'false') {
          setEvergreenOpen(false);
        }
      } catch (e) {
        console.error('Failed to check evergreen registration status', e);
      } finally {
        setCheckingReg(false);
      }
    };
    fetchSettings();
  }, []);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (checkingReg) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="text-forest-green text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mb-4 mx-auto" />
          <p className="font-bold">Opening the academy gate...</p>
        </div>
      </div>
    );
  }

  if (!evergreenOpen) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
        <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-amber py-12">
          <div className="mx-auto w-16 h-16 bg-amber/20 rounded-full flex items-center justify-center mb-4 text-amber">
            <SafeIcon name="Tent" className="text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-2">A Live Academy Is In Session</h1>
          <p className="text-lg text-muted mb-6 leading-relaxed">
            Self-paced registration is paused while a live round of GospelWise Author Academy is underway —
            and the live experience is even better. Join us there!
          </p>
          <a href="https://join.crossandpen.com/p/camp-faibowrimo/" className="block w-full sm:w-auto mt-6">
            <Button className="py-4 px-10 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold shadow-md">
              See the Live Academy
            </Button>
          </a>
          <div className="pt-8 mt-8 border-t border-tan text-sm">
            <span className="text-muted">Already have an Academy account? </span>
            <a href={HUB_LOGIN_URL} className="text-forest-green font-bold hover:underline">
              Log in here
            </a>
          </div>
        </Card>
      </div>
    );
  }

  const fireHubWebhook = async (payload) => {
    try {
      const res = await fetch(HUB_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: import.meta.env.VITE_HUB_WEBHOOK_SECRET || 'gwa_academy_secure_sync_2026_asdasd',
          app_slug: 'camp',
          ...payload,
        }),
      });
      try {
        const data = await res.json();
        if (data && data.token) setAuthToken(data.token);
      } catch (_) {}
    } catch (webhookErr) {
      console.warn('Hub webhook failed, but academy registration succeeded', webhookErr);
    }
  };

  const handleNewUser = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError('Please fill out all fields to step into the academy.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cleanEmail = form.email.trim().toLowerCase();

      const existingRes = await api.get('students_gwaacademy');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];
      const existing = rows.find(
        (u) => (u.email || '').trim().toLowerCase() === cleanEmail
      );
      if (existing) {
        setError('That email already has an Academy account — use the "I Have a GWA Account" tab to step back in.');
        setLoading(false);
        return;
      }

      const trailStart = computeTrailStartDate();

      const profileData = {
        name: form.name,
        email: cleanEmail,
        role: 'camper',
        pass_type: 'evergreen',
        trail_start_date: trailStart,
        first_login_complete: true,
        owner_id: 'pending',
      };

      const createRes = await api.post('students_gwaacademy', profileData);

      const userId =
        createRes?.data?.id ||
        createRes?.id ||
        (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);

      if (userId) {
        await api.put('students_gwaacademy', userId, { owner_id: userId });
      }

      await fireHubWebhook({
        email: cleanEmail,
        name: form.name,
        password: form.password,
        trail_start_date: trailStart,
        source: 'evergreen_start',
      });

      setRegisteredEmail(cleanEmail);
      setStep(2);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleExistingUser = async (e) => {
    e.preventDefault();
    if (!existingEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    const cleanEmail = existingEmail.trim().toLowerCase();

    try {
      const existingRes = await api.get('students_gwaacademy');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];
      const existing = rows.find(
        (u) => (u.email || '').trim().toLowerCase() === cleanEmail
      );

      const trailStart = computeTrailStartDate();

      if (!existing) {
        const createRes = await api.post('students_gwaacademy', {
          name: '',
          email: cleanEmail,
          role: 'camper',
          pass_type: 'evergreen',
          trail_start_date: trailStart,
          first_login_complete: true,
          owner_id: 'pending',
        });
        const newId =
          createRes?.data?.id ||
          createRes?.id ||
          (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);
        if (newId) await api.put('students_gwaacademy', newId, { owner_id: newId });
      } else if (existing.pass_type === 'free') {
        await api.put('students_gwaacademy', existing.id, {
          pass_type: 'evergreen',
          trail_start_date: trailStart,
        });
      }

      await fireHubWebhook({
        email: cleanEmail,
        action: 'grant_access',
        trail_start_date: trailStart,
        source: 'evergreen_start_existing',
      });

      setRegisteredEmail(cleanEmail);
      setStep(2);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const enterCamp = () => {
    if (authToken) {
      window.location.href = `https://camp.gospelwiseauthor.com/?token=${encodeURIComponent(authToken)}`;
    } else {
      window.location.href = HUB_LOGIN_URL;
    }
  };

  const goToAapCheckout = () => {
    window.location.href = `${EVERGREEN_AAP_CHECKOUT_URL}?email=${encodeURIComponent(registeredEmail)}`;
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-forest-green">
        <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mb-4 text-white">
          <SafeIcon name="Compass" className="text-3xl" />
        </div>

        {step === 1 && (
          <>
            <h1 className="text-2xl font-bold text-dark-green">
              Your Week at the Academy Starts Now
            </h1>
            <p className="text-muted">
              Register in under a minute — Day 0 opens the moment you're in.
            </p>

            <div className="flex border-b border-tan mb-6">
              <button
                className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'new' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted hover:text-dark-green bg-gray-50/50'}`}
                onClick={() => { setActiveTab('new'); setError(''); }}
              >
                New Student
              </button>
              <button
                className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'existing' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted hover:text-dark-green bg-gray-50/50'}`}
                onClick={() => { setActiveTab('existing'); setError(''); }}
              >
                I Have a GWA Account
              </button>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md text-center">
                {error}
              </div>
            )}

            {activeTab === 'new' ? (
              <form onSubmit={handleNewUser} className="space-y-4 text-left">
                <Input
                  label="Full Name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g. Jane Doe"
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="you@email.com"
                />
                <Input
                  label="Create a Password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  placeholder="••••••••"
                />
                <div className="pt-4">
                  <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>
                    {loading ? 'Opening the gate...' : 'Step Into the Academy — Free'}
                    {!loading && <SafeIcon name="ArrowRight" className="ml-2" />}
                  </Button>
                </div>
                <p className="text-xs text-muted text-center pt-2">
                  Free for one full week. No card required.
                </p>
              </form>
            ) : (
              <form onSubmit={handleExistingUser} className="space-y-6 text-left">
                <div className="bg-amber/10 p-4 rounded-md border border-amber/30">
                  <p className="text-sm text-dark-green leading-relaxed">
                    Already part of the GospelWise Author family? Enter your email and
                    we'll set your trail — no new account needed.
                  </p>
                </div>
                <Input
                  label="Your GWA Email Address"
                  type="email"
                  value={existingEmail}
                  onChange={(e) => setExistingEmail(e.target.value)}
                  required
                  placeholder="you@email.com"
                />
                <div className="pt-4">
                  <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>
                    {loading ? 'Setting your trail...' : 'Step Into the Academy — Free'}
                  </Button>
                </div>
              </form>
            )}

            <div className="pt-6 mt-4 border-t border-tan text-sm text-center">
              <span className="text-muted">Already registered for this session? </span>
              <a href={HUB_LOGIN_URL} className="text-forest-green font-bold hover:underline">
                Log in here
              </a>
            </div>
          </>
        )}

        {step === 2 && (
          <div className="space-y-5 py-2">
            <div className="mx-auto w-16 h-16 bg-forest-green/10 rounded-full flex items-center justify-center">
              <SafeIcon name="Tent" className="text-3xl text-forest-green" />
            </div>
            <h2 className="text-2xl font-bold text-dark-green">Welcome to the Academy — You're In!</h2>
            <p className="text-muted leading-relaxed">
              Day 0 is open right now: get oriented, set up your profile, and meet
              the four personas. Your Day 1 Morning Lesson arrives tomorrow morning.
            </p>

            <div className="bg-amber/10 border border-amber/30 rounded-md p-5 text-left space-y-3">
              <p className="font-bold text-dark-green">
                One-time offer — the All-Access Pass, just $9 today
              </p>
              <p className="text-sm text-dark-green leading-relaxed">
                Keep everything you build forever — plus personal daily emails
                from Jeremy, the Midday Lab Implementation Library, the private podcast,
                the Self-Letter Studio, your permanent Compass archive, the printable
                Framework Kit, and the GWA Roadmap Course &amp; Publishing Handbook.
              </p>
              <p className="text-xs text-muted">
                $9 right now at registration · $19 during your academy week · closed after
                your week ends.
              </p>
              <Button
                className="w-full py-3 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold"
                onClick={goToAapCheckout}
              >
                Add All-Access — $9
              </Button>
            </div>

            <Button variant="outline" className="w-full py-3 border-2 border-forest-green" onClick={enterCamp}>
              Continue to Academy — Free <SafeIcon name="ArrowRight" className="ml-2" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}